import { storeToRefs } from 'pinia';
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';
import { notifyError, notifySuccess } from '@/core/helpers/notification.helper';
import { useI18n } from 'vue-i18n';
import { fetchJobBoard, updateJobBoard } from '@/core/collections/job-boards/job-board.service';
import { getFilters } from '@/modules/content-blocks/services/positions.service';
import { usePost } from '@/core/hooks/useFetch';
import type { ApiResource } from '@future-harry/nxt-styles';

export function useJobBoard(id?: string) {
   const { t } = useI18n();
   const store = useJobBoardStore();
   const { categoryImages, isFetchFinished, fetchError } = storeToRefs(store);

   const fetchAndSetFilters = async (tenantId: number) => {
      const filters = await getFilters(tenantId);
      store.setFilters(filters);
   };

   const saveCategoryImages = async () => {
      if (categoryImages.value.length > 0) {
         await usePost<ApiResource<void>>(`/job-boards/${id}/category-images/bulk`, {
            data: {
               category_images: categoryImages.value,
            },
         });
      }
   };

   const themeStyles = computed(() => {
      const t = store.theme || ({} as any);
      const styles: Record<string, string | undefined> = {};

      if (t.primary_color) styles['--jobsite-primary'] = t.primary_color;
      if (t.primary_font_color) styles['--jobsite-primary-font'] = t.primary_font_color;
      if (t.secondary_font_color) styles['--jobsite-secondary-font'] = t.secondary_font_color;
      if (t.border_radius) styles['--jobsite-border-radius'] = t.border_radius + 'px';
      if (t.font_family) styles['--jobsite-font-family'] = t.font_family;

      return styles;
   });

   const update = async (disableNotifications = false) => {
      store.isUpdatingJobBoard = true;
      try {
         await saveCategoryImages();
         const jobBoard = await updateJobBoard(id, store.currentJobBoard);
         store.setJobBoard(jobBoard);
         if (!disableNotifications) {
            notifySuccess({ title: t('job-board.settings.save-success') });
         }
      } catch (error) {
         if (!disableNotifications) {
            notifyError({ title: t('job-board.settings.save-error') });
         } else {
            throw error;
         }
      } finally {
         store.isUpdatingJobBoard = false;
      }
   };

   const fetch = async () => {
      try {
         const jobBoard = await fetchJobBoard(id);
         store.setJobBoard(jobBoard);
         await fetchAndSetFilters(jobBoard.tenant_id);

         return jobBoard;
      } catch (err) {
         store.fetchError = err as Error;
      } finally {
         store.isFetchFinished = true;
      }
   };

   return {
      isJobBoardFetchFinished: isFetchFinished,
      error: fetchError,
      updateJobBoard: update,
      fetchJobBoard: fetch,
      themeStyles,
      ...storeToRefs(store),
   };
}
